import _ from "lodash"
import React from "react"
import { useAppSelector } from "../../app/hooks"
import { HandlingApp } from "../../common/pageId"
import { FieldName, FlightText } from "../../common/text"
import {
  categoryOrder,
  EditMode,
  FieldCategory,
  fieldOrder,
  FlightMode,
  PageGroup
} from "../../common/transaction"
import { IUserSettingsSelection } from "../../containers/selectionSlice"
import { CodeType } from "../AirportSelector"
import { showField, showFieldSection } from "./Config"
//import { GabaContext } from "../../pages/addFlights/Module"

interface ShowLabelConfig {
  showLabelSection: boolean
  showLabel: boolean
}

export interface CustomShowLabelConfig {
  terminal: ShowLabelConfig
  parkingLocation: ShowLabelConfig
  serviceTypeAcReg: ShowLabelConfig
}

interface Props {
  editMode: EditMode
  pageGroup: PageGroup
  codeType: CodeType
  flightMode: string
  sticky?: boolean
  isInitResponse?: boolean
  grcMode: string
  customShowLabelConfig: CustomShowLabelConfig
  showOperatorLabel?: boolean
}

const TransactionFlightHeaders: React.FunctionComponent<Props> = ({
  sticky,
  editMode,
  codeType,
  pageGroup,
  flightMode,
  isInitResponse,
  grcMode,
  customShowLabelConfig,
  showOperatorLabel
}) => {
  const { showOptionalFields, useSSIMView }: IUserSettingsSelection =
    useAppSelector((state) => state["selectionReducer"].userSettings)
  const { useLocalTime } =
    useAppSelector(
      (state) => state["selectionReducer"].airportSeasonsSelector
    ) || {}
  const { versionInfo } = useAppSelector((state) => state["appReducer"])

  const isSSIMView = pageGroup === PageGroup.HANDLING ? false : useSSIMView

  // Label Text
  const {
    airport,
    ap,
    operator,
    serviceNo,
    seats,
    clearTime,
    time,
    reqTime,
    st,
    servType,
    f,
    mgt,
    doop,
    dest,
    acReg,
    reason,
    origin,
    last,
    next,
    dateRange,
    date,
    o,
    acType,
    searchTimeRange,
    terminal
  } = FlightText

  const isAdd = editMode === EditMode.ADD
  const isResponse = editMode === EditMode.RESPONSE
  const isEdit = editMode === EditMode.EDIT
  const isTurnaround = flightMode === FlightMode.Turnaround
  const isArrival = flightMode === FlightMode.Arrival
  const isGaba = pageGroup === PageGroup.GABA
  const isHandling = pageGroup === PageGroup.HANDLING
  const isFRPGRHandling =
    versionInfo.handlingApplication === HandlingApp.FRAPORTGR
  const isShl = pageGroup === PageGroup.SHL
  const useICAOCode = codeType === CodeType.ICAO

  const showLabelSection = (fieldName: string) =>
    showFieldSection(
      fieldName,
      editMode,
      pageGroup,
      versionInfo.handlingApplication !== ""
        ? versionInfo.handlingApplication
        : undefined
    )

  const showLabel = (fieldName: string) =>
    showField(fieldName, flightMode, editMode, grcMode)

  const isDateRange =
    showLabelSection(FieldName.startDate) && showLabelSection(FieldName.endDate)

  const getAirportLabel = (key?: number) => {
    return (
      <section
        key={key}
        className={
          "ocs-field-item ocs-field-container " +
          (isAdd
            ? useICAOCode
              ? "trans-field-w-airport-icao"
              : "trans-field-w-airport"
            : useICAOCode
              ? "trans-field-w-airport-readonly-icao"
              : "trans-field-w-airport-readonly")
        }
      >
        {showLabel(FieldName.airport) && <label>{isAdd ? ap : airport}</label>}
      </section>
    )
  }

  const getOperatorLabel = (key?: number) => {
    const showOperLabel =
      showOperatorLabel !== undefined
        ? showOperatorLabel
        : showLabel(FieldName.operatorArr) || showLabel(FieldName.operatorDep)
    return (
      (showLabelSection(FieldName.operatorArr) ||
        showLabelSection(FieldName.operatorDep)) && (
        <section
          key={key}
          className={
            "ocs-field-item ocs-field-container " +
            (isResponse
              ? "trans-field-w-operator-readonly"
              : "trans-field-w-operator")
          }
        >
          {showOperLabel && <label>{operator}</label>}
        </section>
      )
    )
  }

  const getRequestCodeLabel = (key?: number) => {
    return (
      showLabelSection(FieldName.requestCode) && (
        <section
          key={key}
          className="ocs-field-item ocs-field-container trans-field-w-request-code"
        >
          <label>{FlightText.code}</label>
        </section>
      )
    )
  }

  const getACRegLabel = (key?: number) => {
    return (
      (showLabelSection(FieldName.aircraftRegistration) ||
        customShowLabelConfig.serviceTypeAcReg.showLabelSection) && (
        <section
          key={key}
          className="ocs-field-item ocs-field-container trans-field-w-ac-reg"
        >
          {(customShowLabelConfig.serviceTypeAcReg.showLabelSection
            ? customShowLabelConfig.serviceTypeAcReg.showLabel
            : showLabel(FieldName.aircraftRegistration)) && (
            <label>{acReg}</label>
          )}
        </section>
      )
    )
  }

  const getServiceNoLabel = (key?: number) => {
    return (
      (showLabelSection(FieldName.serviceNoArr) ||
        showLabelSection(FieldName.serviceNoDep)) && (
        <section
          key={key}
          className="ocs-field-item ocs-field-container trans-field-w-service-no"
        >
          {(showLabel(FieldName.serviceNoArr) ||
            showLabel(FieldName.serviceNoDep)) && <label>{serviceNo}</label>}
        </section>
      )
    )
  }

  const getServiceTypeLabel = (key?: number) => {
    return (
      (showLabelSection(FieldName.serviceTypeArr) ||
        showLabelSection(FieldName.serviceTypeDep)) && (
        <section
          key={key}
          className={
            "ocs-field-item ocs-field-container " +
            (isResponse
              ? "trans-field-w-service-type-readonly"
              : "trans-field-w-service-type")
          }
        >
          {(showLabel(FieldName.serviceTypeArr) ||
            showLabel(FieldName.serviceTypeDep)) && (
            <label>{pageGroup === PageGroup.HANDLING ? servType : st}</label>
          )}
        </section>
      )
    )
  }

  const getDateLabel = (key?: number) => {
    return (
      <section
        key={key}
        className={
          "ocs-field-container " +
          (isDateRange
            ? "ocs-mr-4 " +
              (isResponse
                ? "trans-field-w-date-range-readonly"
                : "trans-field-w-date-range")
            : "ocs-mr-12 trans-field-w-date")
        }
      >
        <label>{isDateRange ? dateRange : date}</label>
      </section>
    )
  }

  const getReqDateLabel = (key?: number) => {
    return (
      (showLabelSection(FieldName.requiredDateArr) ||
        showLabelSection(FieldName.requiredDateDep)) && (
        <section
          key={key}
          className="ocs-field-container ocs-field-item trans-field-w-req-date"
        >
          <label>{FlightText.reqDate}</label>
        </section>
      )
    )
  }

  const getDoopLabel = (key?: number) => {
    return (
      showLabelSection(FieldName.doop) && (
        <section
          key={key}
          className="ocs-field-item ocs-field-container trans-field-w-doop"
        >
          {showLabel(FieldName.doop) && <label>{doop}</label>}
        </section>
      )
    )
  }

  const getWoopLabel = (key?: number) => {
    return (
      showOptionalFields &&
      showLabelSection(FieldName.woop) && (
        <section
          key={key}
          className="ocs-field-item ocs-field-container trans-field-w-woop"
        >
          {showLabel(FieldName.woop) && <label>{f}</label>}
        </section>
      )
    )
  }

  const getClearTimeLabel = (key?: number) => {
    return (
      <section
        key={key}
        className="ocs-field-item ocs-field-container trans-field-w-cleared-time"
      >
        <label>{isAdd ? time : clearTime}</label>
      </section>
    )
  }

  const getReqTimeLabel = (key?: number) => {
    return (
      (showLabelSection(FieldName.requiredTimeArr) ||
        showLabelSection(FieldName.requiredTimeDep)) && (
        <section
          key={key}
          className="ocs-field-item ocs-field-container trans-field-w-req-time"
        >
          {(showLabel(FieldName.requiredTimeArr) ||
            showLabel(FieldName.requiredTimeDep)) && <label>{reqTime}</label>}
        </section>
      )
    )
  }

  const getReasonCodeLabel = (key?: number) => {
    return (
      (showLabelSection(FieldName.reasonCodeArr) ||
        showLabelSection(FieldName.reasonCodeDep)) && (
        <section
          key={key}
          className="ocs-field-item ocs-field-container trans-field-w-reason-code"
        >
          {(showLabel(FieldName.reasonCodeArr) ||
            showLabel(FieldName.reasonCodeDep)) && <label>{reason}</label>}
        </section>
      )
    )
  }

  const getTurnaroundDaysLabel = (key?: number) => {
    return (
      showOptionalFields &&
      showLabelSection(FieldName.turnaroundDays) && (
        <section
          key={key}
          className="ocs-field-item ocs-field-container trans-field-w-turnaround-days"
        >
          {showLabel(FieldName.turnaroundDays) && <label>{o}</label>}
        </section>
      )
    )
  }

  const getMGTLabel = (key?: number) => {
    return (
      showOptionalFields &&
      showLabelSection(FieldName.minGroundTime) && (
        <section
          key={key}
          className="ocs-field-item ocs-field-container trans-field-w-mgt"
        >
          {showLabel(FieldName.minGroundTime) && <label>{mgt}</label>}
        </section>
      )
    )
  }

  const getACTypeLabel = (key?: number) => {
    return (
      showLabelSection(FieldName.aircraftType) && (
        <section
          key={key}
          className="ocs-field-item ocs-field-container trans-field-w-ac-type"
        >
          {showLabel(FieldName.aircraftType) && <label>{acType}</label>}
        </section>
      )
    )
  }
  const getNumSeatsLabel = (key?: number) => {
    return (
      showLabelSection(FieldName.numSeats) && (
        <section
          key={key}
          className="ocs-field-item ocs-field-container trans-field-w-seats"
        >
          {showLabel(FieldName.numSeats) && <label>{seats}</label>}
        </section>
      )
    )
  }
  const getOrigDestLabel = (key?: number) => {
    return (
      (showLabelSection(FieldName.originStation) ||
        showLabelSection(FieldName.destinationStation)) && (
        <section
          key={key}
          className={
            "ocs-field-item ocs-field-container " +
            (useICAOCode
              ? "trans-field-w-station-icao"
              : "trans-field-w-station")
          }
        >
          {(showLabel(FieldName.originStation) ||
            showLabel(FieldName.destinationStation)) && (
            <label>
              {isTurnaround ? `${origin}/${dest}` : isArrival ? origin : dest}
            </label>
          )}
        </section>
      )
    )
  }

  const getLastNextLabel = (key?: number) => {
    return (
      showOptionalFields &&
      (showLabelSection(FieldName.lastStation) ||
        showLabelSection(FieldName.nextStation)) && (
        <section
          key={key}
          className={
            "ocs-field-item ocs-field-container " +
            (useICAOCode
              ? "trans-field-w-station-icao"
              : "trans-field-w-station")
          }
        >
          {(showLabel(FieldName.lastStation) ||
            showLabel(FieldName.nextStation)) && (
            <label>
              {isTurnaround ? `${last}/${next}` : isArrival ? last : next}
            </label>
          )}
        </section>
      )
    )
  }

  const getOriginLabel = (key?: number) => {
    return (
      showLabelSection(FieldName.originStation) && (
        <section
          key={key}
          className={
            "ocs-field-item ocs-field-container " +
            (useICAOCode
              ? "trans-field-w-station-icao"
              : "trans-field-w-station")
          }
        >
          {showLabel(FieldName.originStation) && <label>{origin}</label>}
        </section>
      )
    )
  }

  const getDestinationLabel = (key?: number) => {
    return (
      showLabelSection(FieldName.destinationStation) && (
        <section
          key={key}
          className={
            "ocs-field-item ocs-field-container " +
            (useICAOCode
              ? "trans-field-w-station-icao"
              : "trans-field-w-station")
          }
        >
          {showLabel(FieldName.destinationStation) && <label>{dest}</label>}
        </section>
      )
    )
  }

  const getLastLabel = (key?: number) => {
    return (
      showOptionalFields &&
      showLabelSection(FieldName.lastStation) && (
        <section
          key={key}
          className={
            "ocs-field-item ocs-field-container " +
            (useICAOCode
              ? "trans-field-w-station-icao"
              : "trans-field-w-station")
          }
        >
          {showLabel(FieldName.lastStation) && <label>{last}</label>}
        </section>
      )
    )
  }

  const getNextLabel = (key?: number) => {
    return (
      showOptionalFields &&
      showLabelSection(FieldName.nextStation) && (
        <section
          key={key}
          className={
            "ocs-field-item ocs-field-container " +
            (useICAOCode
              ? "trans-field-w-station-icao"
              : "trans-field-w-station")
          }
        >
          {showLabel(FieldName.nextStation) && <label>{next}</label>}
        </section>
      )
    )
  }

  const getSearchTimeLabel = (key?: number) => {
    return (
      showOptionalFields &&
      (showLabelSection(FieldName.offerTimeArr) ||
        showLabelSection(FieldName.offerTimeDep)) && (
        <section
          key={key}
          className="ocs-mr-4 ocs-field-container trans-field-w-search-time-range"
        >
          {(showLabel(FieldName.offerTimeArr) ||
            showLabel(FieldName.offerTimeDep)) && (
            <label>{searchTimeRange}</label>
          )}
        </section>
      )
    )
  }

  const getHandlerLabel = (key?: number) => {
    return (
      showLabelSection(FieldName.handler) && (
        <section
          key={key}
          className={"ocs-field-item ocs-field-container trans-field-w-handler"}
        >
          {showLabel(FieldName.handler) && <label>{FlightText.handler}</label>}
        </section>
      )
    )
  }

  const getMTOWLabel = (key?: number) => {
    return (
      showLabelSection(FieldName.mtow) && (
        <section
          key={key}
          className={"ocs-field-item ocs-field-container trans-field-w-mtow"}
        >
          {showLabel(FieldName.mtow) && <label>{FlightText.mtow}</label>}
        </section>
      )
    )
  }

  const getOperatorTextLabel = (key?: number) => {
    return (
      showLabelSection(FieldName.operatorText) && (
        <section
          key={key}
          className={
            "ocs-field-item ocs-field-container trans-field-w-operator-text"
          }
        >
          {showLabel(FieldName.operatorText) && (
            <label>{FlightText.operatorTextCaption}</label>
          )}
        </section>
      )
    )
  }

  const getParkingLocationLabel = (key?: number) => {
    return (
      customShowLabelConfig.parkingLocation.showLabelSection &&
      showLabelSection(FieldName.parkingLocation) && (
        <section
          key={key}
          className={
            "ocs-field-item ocs-field-container trans-field-w-parking-loc"
          }
        >
          {showLabel(FieldName.parkingLocation) &&
            customShowLabelConfig.parkingLocation.showLabel && (
              <label>{FlightText.parkLoc}</label>
            )}
        </section>
      )
    )
  }

  const getVfrLabel = (key?: number) => {
    return (
      showLabelSection(FieldName.vfr) && (
        <section
          key={key}
          className={
            "ocs-field-item ocs-field-container trans-field-w-flight-rules"
          }
        >
          {showLabel(FieldName.vfr) && <label>{FlightText.vfrCaption}</label>}
        </section>
      )
    )
  }

  const getTowBarLabel = (key?: number) => {
    return (
      showLabelSection(FieldName.towbar) && (
        <section
          key={key}
          className={"ocs-field-item ocs-field-container trans-field-w-towbar"}
        >
          {showLabel(FieldName.towbar) && <label>{FlightText.towbar}</label>}
        </section>
      )
    )
  }

  const getGabaLoungeLabel = (key?: number) => {
    return (
      showOptionalFields &&
      showLabelSection(FieldName.gabaLounge) && (
        <section
          key={key}
          className={"ocs-field-item ocs-field-container trans-field-w-lounge"}
        >
          {showLabel(FieldName.gabaLounge) && (
            <label>{FlightText.gabaLounge}</label>
          )}
        </section>
      )
    )
  }

  const getRefuelLabel = (key?: number) => {
    return (
      showOptionalFields &&
      showLabelSection(FieldName.refuelling) && (
        <section
          key={key}
          className={"ocs-field-item ocs-field-container trans-field-w-refuel"}
        >
          {showLabel(FieldName.refuelling) && (
            <label>{FlightText.refuelling}</label>
          )}
        </section>
      )
    )
  }

  const getNoiseCertificateLabel = (key?: number) => {
    return (
      showOptionalFields &&
      showLabelSection(FieldName.noiseCertificate) && (
        <section
          key={key}
          className={"ocs-field-item ocs-field-container trans-field-w-noise"}
        >
          {showLabel(FieldName.noiseCertificate) && (
            <label>{FlightText.noise}</label>
          )}
        </section>
      )
    )
  }

  const getTerminalLabel = (key?: number) => {
    return (
      customShowLabelConfig.terminal.showLabelSection &&
      (showLabelSection(FieldName.terminalArr) ||
        showLabelSection(FieldName.terminalDep)) && (
        <section
          key={key}
          className="ocs-field-item ocs-field-container trans-field-w-terminal"
        >
          {customShowLabelConfig.terminal.showLabel &&
            (showLabel(FieldName.terminalArr) ||
              showLabel(FieldName.terminalDep)) && <label>{terminal}</label>}
        </section>
      )
    )
  }

  const labelMap = {
    [FieldName.airport]: getAirportLabel,
    [FieldName.operatorArr]: getOperatorLabel,
    [FieldName.requestCode]: getRequestCodeLabel,
    [FieldName.reqDate]: getReqDateLabel,
    [FieldName.aircraftRegistration]: getACRegLabel,
    [FieldName.serviceNoArr]: getServiceNoLabel,
    [FieldName.serviceTypeArr]: getServiceTypeLabel,
    [FieldName.startDate]: getDateLabel,
    [FieldName.doop]: getDoopLabel,
    [FieldName.woop]: getWoopLabel,
    [FieldName.clearedTimeArr]: getClearTimeLabel,
    [FieldName.requiredTimeArr]: getReqTimeLabel,
    [FieldName.reasonCodeArr]: getReasonCodeLabel,
    [FieldName.turnaroundDays]: getTurnaroundDaysLabel,
    [FieldName.minGroundTime]: getMGTLabel,
    [FieldName.aircraftType]: getACTypeLabel,
    [FieldName.numSeats]: getNumSeatsLabel,
    [FieldName.origDest]: getOrigDestLabel,
    [FieldName.lastNext]: getLastNextLabel,
    [FieldName.originStation]: getOriginLabel,
    [FieldName.lastStation]: getLastLabel,
    [FieldName.destinationStation]: getDestinationLabel,
    [FieldName.nextStation]: getNextLabel,
    [FieldName.offerTimeArr]: getSearchTimeLabel,
    [FieldName.handler]: getHandlerLabel,
    [FieldName.parkingLocation]: getParkingLocationLabel,
    [FieldName.vfr]: getVfrLabel,
    [FieldName.towbar]: getTowBarLabel,
    [FieldName.noiseCertificate]: getNoiseCertificateLabel,
    [FieldName.gabaLounge]: getGabaLoungeLabel,
    [FieldName.refuelling]: getRefuelLabel,
    [FieldName.mtow]: getMTOWLabel,
    [FieldName.operatorText]: getOperatorTextLabel,
    [FieldName.terminalArr]: getTerminalLabel
  }

  const headingTitleMap = {
    [FieldCategory.Airport]: isAdd ? "Airport" : "A/P",
    [FieldCategory.Period]: isGaba || isHandling ? "Date" : "Period",
    [FieldCategory.Handling]: isFRPGRHandling ? "Additional" : "Handling"
  }

  const handleGetLabels = () => {
    let labels = []
    const orderFormat = isSSIMView ? "SSIMOrder" : "categoryOrder"

    const orderedLabels = _.cloneDeep(fieldOrder).sort((a, b) => {
      return (
        ((isGaba || isHandling) && a.gabaOrder ? a.gabaOrder : a[orderFormat]) -
        ((isGaba || isHandling) && b.gabaOrder ? b.gabaOrder : b[orderFormat])
      )
    })

    if (isSSIMView) {
      orderedLabels.forEach((f, idx) => {
        if (labelMap[f.type] && f[orderFormat] >= 0) {
          labels.push(labelMap[f.type](idx))
        }
      })
    } else {
      _.cloneDeep(categoryOrder).forEach((cat, idx) => {
        const hideSearchTime =
          cat === FieldCategory.SearchTime &&
          (!showOptionalFields || !showLabelSection(FieldName.offerTimeArr))
        const hideHandling =
          cat === FieldCategory.Handling &&
          !showLabelSection(FieldName.handler) &&
          !showLabelSection(FieldName.parkingLocation)
        if (!hideSearchTime && !hideHandling) {
          const categoryLabels = orderedLabels
            .filter(
              (l) =>
                l[orderFormat] >= 0 &&
                ((isGaba || isHandling) && l.gabaCategory
                  ? l.gabaCategory === cat
                  : l.category === cat)
            )
            .map((cl, idx) =>
              labelMap[cl.type] ? labelMap[cl.type](idx) : null
            )
          labels.push(
            <div key={idx}>
              <div className="trans-fields-heading">
                <p>{headingTitleMap[cat] ?? cat}</p>
                <span />
              </div>
              <span className="d-flex">{categoryLabels}</span>
            </div>
          )
        }
      })
    }

    return labels
  }

  return (
    <div
      className={
        "d-flex " +
        (isEdit
          ? "trans-fields-headings-ml-edit"
          : isResponse
            ? isShl
              ? "trans-fields-headings-ml-shl-response"
              : "trans-fields-headings-ml-response"
            : "") +
        (sticky ? " trans-fields-headings-sticky" : "") +
        (isSSIMView && (isEdit || isResponse)
          ? " trans-fields-headings-ssim"
          : "") +
        (useLocalTime ? " trans-fields-headings-local-time" : "")
      }
    >
      {isResponse &&
        isInitResponse &&
        (isSSIMView ? (
          <section
            className={
              "ocs-field-item ocs-field-container trans-field-w-reply-code"
            }
          >
            <label>Action</label>
          </section>
        ) : (
          <div>
            <div className="trans-fields-heading">
              <p>Commit</p>
              <span />
            </div>
            <span className="d-flex">
              <section
                className={
                  "ocs-field-item ocs-field-container trans-field-w-reply-code"
                }
              >
                <label>Action</label>
              </section>
            </span>
          </div>
        ))}
      {handleGetLabels()}
    </div>
  )
}

export default TransactionFlightHeaders
